import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import { ListGroupItem, ListGroup, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Alert } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { serviceName } from '../../../shared/appointmint/appointment';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExtraConfigurationModal from './ExtraModal';
import { ExtrasSettingsData, ExtraData } from '../../../models';
import { Guid } from '../../../shared/utils';
import AppButton from '../../../components/AppButton';

export interface ExtrasSettingsProps extends SettingsItemContentProps {
    values: ExtrasSettingsData,
    onChange: (settings: ExtrasSettingsData, save?: boolean) => void,
}

const generateEmptyExtraData = (): ExtraData => ({
    id: Guid.newGuid(),
    creationDate: new Date().toISOString(),
    name: '',
    category: '',
    appointmentTypes: [],
    priceForClient: 0,
    professionalPart: 0,
    stockItems: [],
})

const ExtrasSettings : FC<ExtrasSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const extras = values.extras

    const [editingExtra, setEditingExtra] = useState<ExtraData|undefined>(undefined);
    const [deletingId, setDeletingId] = React.useState<string|undefined>(undefined);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const closeDeleteModal = React.useCallback(() => setDeletingId(undefined), [deletingId]);
    const onAddNew = useCallback(() => setEditingExtra(generateEmptyExtraData()), [setEditingExtra]);
    const onSelect = useCallback((extra: ExtraData) => () => setEditingExtra(extra), [setEditingExtra]);
    const onCloseExtra = useCallback(() => setEditingExtra(undefined), [setEditingExtra]);

    const toggleCategory = useCallback((category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    }, []);

    const onSaveExtra = useCallback((newExtra?: ExtraData) => {
        const addingNewExtra = newExtra && !extras.find(e => e.id === newExtra.id)
        onChange({
            ...values,
            extras: !newExtra ? extras.filter(r => r.id !== deletingId)
                : addingNewExtra ? [...extras, { ...newExtra }]
                : extras.map(r => r.id === editingExtra?.id ? { ...newExtra } : r),
            }, true);
        setDeletingId(undefined);
        setEditingExtra(undefined);
    }, [editingExtra, extras, deletingId, values, onChange]);

    const sortedExtras = useMemo(
        () => [...extras].sort((a, b) => a.name.localeCompare(b.name)),
        [extras]
    );

    const { categorizedGroups, uncategorizedExtras, sortedCategoryNames } = useMemo(() => {
        const groups: Record<string, ExtraData[]> = {};
        const uncategorized: ExtraData[] = [];
        for (const extra of sortedExtras) {
            const cat = extra.category?.trim();
            if (cat) {
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(extra);
            } else {
                uncategorized.push(extra);
            }
        }
        return {
            categorizedGroups: groups,
            uncategorizedExtras: uncategorized,
            sortedCategoryNames: Object.keys(groups).sort((a, b) => a.localeCompare(b)),
        };
    }, [sortedExtras]);

    const renderExtraRow = (extra: ExtraData) => (
        <ListGroupItem key={extra.id}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacer-XXS)', minWidth: 0 }}>
                <AppButton id={`${id}-open-extra-${extra.id}-button`} onClick={onSelect(extra)} color="link" style={{ padding: 0, textAlign: 'left', justifyContent: 'flex-start' }}>{extra.name}</AppButton>
                {extra.appointmentTypes.length > 0 && (
                    <div style={{ display: 'flex', gap: 'var(--spacer-XS)', flexWrap: 'wrap' }}>
                        {extra.appointmentTypes.map(type => <Badge key={type}>{serviceName(type, generalSettings)}</Badge>)}
                    </div>
                )}
            </div>
        </ListGroupItem>
    );

    return (
        <>
            <Alert color="secondary" isOpen>
                {t(TK.extrasSettingsDescription)}
            </Alert>

            <ListGroup>
                {uncategorizedExtras.map(renderExtraRow)}
                {sortedCategoryNames.map(category => (
                    <React.Fragment key={category}>
                        <ListGroupItem
                            onClick={() => toggleCategory(category)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: 'var(--bg-light)',
                                paddingTop: 'var(--spacer-S)',
                                paddingBottom: 'var(--spacer-S)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                                {expandedCategories.has(category)
                                    ? <ExpandMoreIcon fontSize="small" />
                                    : <ChevronRightIcon fontSize="small" />}
                                <span style={{ fontWeight: 600 }}>{category}</span>
                                <Badge color="secondary">{categorizedGroups[category].length}</Badge>
                            </div>
                        </ListGroupItem>
                        {expandedCategories.has(category) && categorizedGroups[category].map(renderExtraRow)}
                    </React.Fragment>
                ))}
                <ListGroupItem>
                    <IconButton id={`${id}-add-button`} onClick={onAddNew} size="small"><AddCircleIcon/>&nbsp;{t(TK.new)}</IconButton>
                </ListGroupItem>
            </ListGroup>

            <ExtraConfigurationModal 
                defaultValue={editingExtra}
                onSave={onSaveExtra}
                onClose={onCloseExtra}
                onDelete={setDeletingId}
            />
            
            <Modal isOpen={!!deletingId} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`${id}-confirm-delete-button`} color="danger" onClick={() => onSaveExtra()}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default ExtrasSettings;